<template>
    <main-dialog
        ref="dialog"
        heading="planning.copyCampaign.heading"
        btn-confirm-text="actions.copy"
        :has-activator="hasActivator"
        @confirm="copyCampaignDialogConfirm"
        @close="closeDialog"
    >
        <template v-if="hasActivator" v-slot:actions="{ activator }">
            <icon v-bind="activator" icon-name="custom:duplicate" small @click.stop />
        </template>
        <template v-slot:default>
            <div class="copy-campaign">
                <p class="copy-campaign__description">
                    {{ toSentenceCase($t('planning.copyCampaign.description')) }}
                </p>
                <p class="copy-campaign__form-item border">
                    <span class="copy-campaign__form-item--label">
                        {{ toSentenceCase($t('entities.campaigns', 1)) }}
                    </span>
                    <vuex-checkbox
                        disabled
                        :getter="() => modelsGetter({ model: copyCampaignModels.campaignModel })"
                        :setter="
                            value =>
                                modelsSetter({ model: copyCampaignModels.campaignModel, value })
                        "
                    />
                </p>
                <template v-if="generalConfig.allowSubCampaignCopying">
                    <p class="copy-campaign__form-item border">
                        <span class="copy-campaign__form-item--label">
                            {{ toSentenceCase($t('entities.subCampaigns', 2)) }}
                        </span>
                        <vuex-checkbox
                            :getter="
                                () =>
                                    modelsGetter({
                                        model: copyCampaignModels.subCampaignsModel,
                                    })
                            "
                            :setter="
                                value =>
                                    modelsSetter({
                                        model: copyCampaignModels.subCampaignsModel,
                                        value,
                                    })
                            "
                        />
                    </p>
                </template>
                <copy-start-dates-list
                    :model-value="modelsGetter({ model: copyCampaignModels.startDatesModel })"
                    label="planning.copyCampaign.startDateField"
                    :min-date="minDate"
                    :max="maxCopyStartDates"
                    :allow-multiple="!!generalConfig.allowMultipleCampaignCopyDates"
                    @update:model-value="
                        value => modelsSetter({ model: copyCampaignModels.startDatesModel, value })
                    "
                />
            </div>
        </template>
    </main-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import CopyCampaignModels from '@enums/copy-campaign-models';
import copyLimits from '@enums/copy-limits';

const { maxCopyStartDates } = copyLimits;

export default {
    props: {
        hasActivator: {
            type: Boolean,
            default: true,
        },
        campaignId: {
            type: String,
            required: true,
        },
        startDate: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            models: {
                [CopyCampaignModels.campaignModel]: true,
                [CopyCampaignModels.subCampaignsModel]: false,
                [CopyCampaignModels.startDatesModel]: [],
            },
            copyCampaignModels: CopyCampaignModels,
            maxCopyStartDates,
        };
    },
    computed: {
        ...mapState('clientConfig', ['generalConfig']),
        minDate() {
            return this.datepickerFormat(this.startDate);
        },
    },
    mounted() {
        this.models[CopyCampaignModels.startDatesModel] = [this.minDate];
    },
    methods: {
        ...mapActions('campaigns', ['copyCampaign']),
        openDialog() {
            this.$refs.dialog.open();
        },
        closeDialog() {
            this.$refs.dialog.close();
            this.resetModels();
        },
        modelsGetter({ model }) {
            return this.models[model];
        },
        modelsSetter({ model, value }) {
            this.models[model] = value;
        },
        async copyCampaignDialogConfirm() {
            const error = await this.copyCampaign({
                id: this.campaignId,
                body: {
                    startDates: this.models[CopyCampaignModels.startDatesModel],
                    includeSubCampaigns: this.models[CopyCampaignModels.subCampaignsModel],
                },
            });

            if (!error) {
                this.closeDialog();
                this.resetModels();
            }
        },
        resetModels() {
            this.modelsSetter({
                model: CopyCampaignModels.startDatesModel,
                value: [this.minDate],
            });
            this.modelsSetter({
                model: CopyCampaignModels.subCampaignsModel,
                value: false,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.copy-campaign {
    font-size: 1.2rem;

    &__description {
        padding: 1rem 0;
    }

    &__form-item {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: 1rem 2rem;

        &--label {
            margin-right: 1rem;
            font-weight: bold;
        }
    }

    .border {
        border-bottom: 0.1rem solid $promo-divider-colour;
    }
}
</style>
